import type { RealEstateKpis } from '@gozoki/api-types'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import {
  uploadStoreProjectDocument,
  uploadStoreProjectPhoto,
} from '../mutations/storeProjects.mutations'
import { keys } from '../queries/keys'
import {
  fetchRealEstateKpis,
  type FetchRealEstateKpisParams,
  type StoreProjectResponse,
} from '../queries/storeProjects.queries'

/**
 * La liste des projets est servie par Tuyau (`api.storeProjects.index`) : ces mutations restées
 * sur l'ancien client doivent viser sa clé pour continuer à rafraîchir le cache.
 */
const storeProjectsListFilter = { queryKey: keys.storeProjects.tuyauList() }

const setStoreProjectsList = (
  queryClient: QueryClient,
  updater: (old: StoreProjectResponse[] | undefined) => StoreProjectResponse[] | undefined,
) => queryClient.setQueriesData<StoreProjectResponse[]>(storeProjectsListFilter, updater)

export const useRealEstateKpis = (
  params?: FetchRealEstateKpisParams,
  enabled = true,
): UseQueryResult<RealEstateKpis, unknown> =>
  useQuery({
    queryKey: [
      'real-estate-kpis',
      params?.openStoresComparisonDays ?? 'default',
      params?.storeIds ?? [],
      params?.ageStoreIds ?? [],
      params?.storeAgeFilter ?? 'default',
      params?.startDate ?? null,
      params?.endDate ?? null,
    ],
    queryFn: () => fetchRealEstateKpis(params),
    enabled,
  })

export const useUploadStoreProjectPhoto = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: uploadStoreProjectPhoto,
    onSuccess: (data, variables) => {
      // Optimistically update the cache with the new photoUrls returned from the server
      setStoreProjectsList(queryClient, (old) => {
        if (!old) return old
        return old.map((project) =>
          project.id === variables.id ? { ...project, photoUrls: data.photoUrls } : project,
        )
      })

      // Also refetch in background to ensure full consistency
      queryClient.invalidateQueries(storeProjectsListFilter)
    },
  })
}

export const useUploadStoreProjectDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: uploadStoreProjectDocument,
    onSuccess: (data, variables) => {
      // Optimistically update the cache with the new documents returned from the server
      setStoreProjectsList(queryClient, (old) => {
        if (!old) return old
        return old.map((project) =>
          project.id === variables.id ? { ...project, documents: data.documents } : project,
        )
      })

      // Also refetch in background to ensure full consistency
      queryClient.invalidateQueries(storeProjectsListFilter)
    },
  })
}
